import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { T } from "../../theme";
import { getCompany } from "../../data/companies";
import { Shell } from "./ui";

/* ─── cores por área ─── */
const AREA_COLORS = {
  Tecnologia: { bg: "rgba(109,40,217,.14)", color: "#9B51FF", border: "rgba(109,40,217,.28)" },
  Design: { bg: "rgba(236,72,153,.12)", color: "#EC4899", border: "rgba(236,72,153,.28)" },
  Marketing: { bg: "rgba(245,158,11,.12)", color: "#F59E0B", border: "rgba(245,158,11,.28)" },
  Dados: { bg: "rgba(6,182,212,.12)", color: "#06B6D4", border: "rgba(6,182,212,.28)" },
  Produto: { bg: "rgba(16,185,129,.12)", color: "#10B981", border: "rgba(16,185,129,.28)" },
  Financeiro: { bg: "rgba(52,211,153,.1)", color: "#34D399", border: "rgba(52,211,153,.25)" },
  Vendas: { bg: "rgba(59,130,246,.12)", color: "#60A5FA", border: "rgba(59,130,246,.28)" },
  Operações: { bg: "rgba(148,163,184,.1)", color: "#94A3B8", border: "rgba(148,163,184,.25)" },
};
const DEFAULT_AREA = { bg: "rgba(155,81,255,.12)", color: "#9B51FF", border: "rgba(155,81,255,.28)" };
const areaColor = (a) => AREA_COLORS[a] || DEFAULT_AREA;

const popIn = keyframes`from { opacity: 0; transform: scale(.97) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); }`;

/* ══════════ wrapper ══════════ */
const Section = styled.section`
  scroll-margin-top: 70px;
  padding: clamp(48px, 7vw, 80px) 0;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 22px;
`;

const Title = styled.h2`
  font-size: clamp(1.5rem, 3.4vw, 2.2rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  color: ${T.text};
  line-height: 1.12;

  span { color: var(--purple); }
`;

const SubTitle = styled.p`
  font-size: 0.9rem;
  color: ${T.muted};
  margin-top: 6px;
`;

/* ══════════ filtros ══════════ */
const FilterStack = styled.div`
  position: sticky;
  top: 62px;
  z-index: 50;
  background: color-mix(in srgb, var(--bg) 92%, transparent);
  backdrop-filter: blur(10px);
  padding: 12px 0;
  border-bottom: 1px solid ${T.border};
  margin-bottom: 8px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 13px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;

  ${(p) =>
    p.$on
      ? css`background: var(--purple); border: 1.5px solid var(--purple); color: #fff; box-shadow: 0 2px 10px rgba(155,81,255,.25);`
      : css`background: transparent; border: 1.5px solid ${T.border}; color: ${T.muted};
            &:hover { border-color: var(--purple); color: var(--purple); background: var(--purple-soft); }`}
`;

const ChipCount = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? "rgba(255,255,255,.22)" : "var(--card2)")};
  color: ${(p) => (p.$on ? "#fff" : T.muted2)};
`;

const SubFilters = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const Select = styled.select`
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${T.text};
  background: ${T.card};
  border: 1.5px solid ${T.border};
  border-radius: 9px;
  padding: 7px 30px 7px 12px;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238980A8' stroke-width='3' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  transition: border-color 0.14s;

  &:hover, &:focus { border-color: var(--purple); }
`;

const ClearBtn = styled.button`
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--purple);
  background: none;
  border: none;
  cursor: pointer;
  padding: 7px 4px;
  &:hover { text-decoration: underline; }
`;

/* ══════════ lista ══════════ */
const ResultMeta = styled.p`
  font-size: 0.74rem;
  color: ${T.muted2};
  padding: 14px 0 14px;
  letter-spacing: 0.01em;
`;

const GroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 0 10px;
`;

const GroupName = styled.h3`
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${T.muted2};
`;

const GroupBadge = styled.span`
  font-size: 0.63rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 5px;
  background: ${T.card2};
  color: ${T.muted2};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Job = styled.button`
  all: unset;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0 12px;
  align-items: center;
  width: 100%;
  padding: 20px 22px;
  background: ${T.card};
  border: 1.5px solid ${T.border};
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  position: relative;
  overflow: hidden;
  transition: border-color 0.16s, box-shadow 0.16s, transform 0.14s;

  &::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, var(--purple), #7c3aed);
    border-radius: 16px 0 0 16px;
    opacity: 0;
    transition: opacity 0.16s;
  }

  &:hover {
    border-color: color-mix(in srgb, var(--purple) 40%, transparent);
    box-shadow: 0 6px 28px rgba(155, 81, 255, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    &::before { opacity: 1; }
  }
  &:active { transform: translateY(0); }
  animation: ${popIn} 0.3s ease both;
  animation-delay: ${(p) => (p.$i || 0) * 0.04}s;
`;

const JLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const JTop = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
`;

const AreaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.71rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
  letter-spacing: 0.02em;
  flex-shrink: 0;

  &::before {
    content: "";
    width: 5px; height: 5px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`;

const CompanyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.71rem;
  font-weight: 700;
  padding: 3px 10px 3px 4px;
  border-radius: 999px;
  background: ${(p) => p.$c}1f;
  color: ${T.text};
  border: 1px solid ${(p) => p.$c}40;
  flex-shrink: 0;

  img {
    width: 16px;
    height: 16px;
    border-radius: 5px;
    object-fit: cover;
    background: #fff;
  }
`;

const CompanyDot = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.52rem;
  font-weight: 800;
  color: #fff;
  background: ${(p) => p.$c};
`;

const LocalBadge = styled.span`
  font-size: 0.71rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 7px;
  background: ${T.card2};
  color: ${T.muted};
  border: 1px solid ${T.border};
  flex-shrink: 0;
`;

const JTitle = styled.h4`
  font-size: 1.02rem;
  font-weight: 700;
  color: ${T.text};
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const JSummary = styled.p`
  font-size: 0.82rem;
  color: ${T.muted};
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${T.muted};
  & + &::before { content: "·"; margin: 0 7px; color: ${T.muted2}; }
`;

const SalaryTag = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #4ade80;
`;

const Sep = styled.span`
  font-size: 0.75rem;
  color: ${T.muted2};
  margin: 0 6px;
  user-select: none;
`;

const Arrow = styled.span`
  font-size: 0.95rem;
  color: ${T.muted2};
  flex-shrink: 0;
  transition: color 0.14s, transform 0.14s;
  ${Job}:hover & { color: var(--purple); transform: translateX(4px); }
`;

/* ── skeleton / estados ── */
const SkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 22px;
  border: 1.5px solid ${T.border};
  border-radius: 16px;
  background: ${T.card};
`;

const SkLine = styled.div`
  height: 12px;
  border-radius: 6px;
  background: ${T.card2};
  width: ${(p) => p.$w || "100%"};
  animation: pulse 1.6s ease-in-out infinite;
  @keyframes pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 0.22; } }
`;

const StateBox = styled.div`
  text-align: center;
  padding: 72px 24px;

  h3 { font-size: 1.04rem; font-weight: 700; color: ${T.text}; margin-bottom: 8px; }
  p { font-size: 0.87rem; color: ${T.muted}; line-height: 1.65; max-width: 340px; margin: 0 auto 22px; }

  button, a {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--purple); color: #fff; font-weight: 700; font-size: 0.84rem;
    font-family: inherit; border: none; padding: 11px 22px; border-radius: 10px;
    cursor: pointer; text-decoration: none;
    box-shadow: 0 3px 14px rgba(155, 81, 255, 0.28);
    transition: filter 0.14s, transform 0.12s;
    &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  }
`;

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

export default function Roles({ jobs, loading, apiErr, search }) {
  const navigate = useNavigate();
  const [area, setArea] = useState("Todas");
  const [nivel, setNivel] = useState("");
  const [regime, setRegime] = useState("");

  const areas = useMemo(() => ["Todas", ...uniq(jobs.map((j) => j.area))], [jobs]);
  const niveis = useMemo(() => uniq(jobs.map((j) => j.nivel)), [jobs]);
  const regimes = useMemo(() => uniq(jobs.map((j) => j.regime)), [jobs]);

  const areaCounts = useMemo(
    () =>
      jobs.reduce((acc, j) => {
        const a = j.area || "Outras";
        acc[a] = (acc[a] || 0) + 1;
        return acc;
      }, {}),
    [jobs]
  );

  const q = search.trim().toLowerCase();
  const visible = jobs.filter((j) => {
    const byArea = area === "Todas" || j.area === area;
    const byNivel = !nivel || j.nivel === nivel;
    const byRegime = !regime || j.regime === regime;
    const bySearch =
      !q || `${j.title} ${j.area} ${j.regime} ${j.nivel} ${j.resumo || ""}`.toLowerCase().includes(q);
    return byArea && byNivel && byRegime && bySearch;
  });

  const grouped = visible.reduce((acc, job) => {
    const key = job.area || "Outras";
    (acc[key] = acc[key] || []).push(job);
    return acc;
  }, {});

  const hasFilter = area !== "Todas" || nivel || regime || q;
  const clearAll = () => { setArea("Todas"); setNivel(""); setRegime(""); };

  return (
    <Section id="vagas" aria-labelledby="roles-title">
      <Shell>
        <HeadRow>
          <div>
            <Title id="roles-title">
              Vagas <span>abertas</span>
            </Title>
            <SubTitle>
              {loading ? "Carregando oportunidades…" : `${jobs.length} oportunidade${jobs.length !== 1 ? "s" : ""} esperando por você.`}
            </SubTitle>
          </div>
        </HeadRow>
      </Shell>

      <Shell>
        {/* ── FILTROS ── */}
        {!loading && !apiErr && jobs.length > 0 && (
          <FilterStack role="group" aria-label="Filtrar vagas">
            <ChipRow>
              {areas.map((a) => (
                <Chip key={a} $on={area === a} onClick={() => setArea(a)} aria-pressed={area === a}>
                  {a}
                  {a !== "Todas" && areaCounts[a] && (
                    <ChipCount $on={area === a} aria-hidden="true">{areaCounts[a]}</ChipCount>
                  )}
                </Chip>
              ))}
            </ChipRow>

            {(niveis.length > 0 || regimes.length > 0) && (
              <SubFilters>
                {niveis.length > 0 && (
                  <Select value={nivel} onChange={(e) => setNivel(e.target.value)} aria-label="Filtrar por nível">
                    <option value="">Todos os níveis</option>
                    {niveis.map((n) => <option key={n} value={n}>{n}</option>)}
                  </Select>
                )}
                {regimes.length > 0 && (
                  <Select value={regime} onChange={(e) => setRegime(e.target.value)} aria-label="Filtrar por regime">
                    <option value="">Todos os regimes</option>
                    {regimes.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                )}
                {hasFilter && <ClearBtn onClick={clearAll}>Limpar filtros</ClearBtn>}
              </SubFilters>
            )}
          </FilterStack>
        )}

        {/* ── LISTA ── */}
        {loading ? (
          <List>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkCard key={i}>
                <SkLine $w="96px" />
                <SkLine $w={`${44 + (i % 3) * 14}%`} style={{ height: 17 }} />
                <SkLine $w="72%" />
                <SkLine $w="52%" />
              </SkCard>
            ))}
          </List>
        ) : apiErr ? (
          <StateBox>
            <h3>Não foi possível carregar as vagas.</h3>
            <p>Problema de conexão com o servidor. Tente recarregar a página.</p>
            <button onClick={() => window.location.reload()}>Tentar novamente</button>
          </StateBox>
        ) : visible.length === 0 ? (
          <StateBox>
            <h3>{hasFilter ? "Nenhuma vaga para esse filtro." : "Nenhuma vaga aberta no momento."}</h3>
            <p>
              {hasFilter
                ? "Tente outro termo ou remova os filtros."
                : "Mande seu portfólio — entramos em contato quando surgir a oportunidade certa."}
            </p>
            {hasFilter ? (
              <button onClick={clearAll}>Limpar filtros</button>
            ) : (
              <a href="/banco-de-talentos">Entrar no banco de talentos →</a>
            )}
          </StateBox>
        ) : (
          <>
            <ResultMeta aria-live="polite" aria-atomic="true">
              {visible.length} vaga{visible.length !== 1 ? "s" : ""}
              {area !== "Todas" ? ` em ${area}` : ""}
              {nivel ? ` · ${nivel}` : ""}
              {regime ? ` · ${regime}` : ""}
              {q ? ` · "${search}"` : ""}
            </ResultMeta>

            {Object.entries(grouped).map(([areaName, list]) => (
              <div key={areaName}>
                {Object.keys(grouped).length > 1 && (
                  <GroupLabel>
                    <GroupName>{areaName}</GroupName>
                    <GroupBadge>{list.length}</GroupBadge>
                  </GroupLabel>
                )}
                <List>
                  {list.map((job, idx) => {
                    const jc = areaColor(job.area);
                    const co = getCompany(job.empresa);
                    return (
                      <Job key={job.id} $i={idx} onClick={() => navigate(`/vagas/${job.slug}`)} aria-label={`Ver vaga: ${job.title} — ${co.name}`}>
                        <JLeft>
                          <JTop>
                            <CompanyBadge $c={co.color}>
                              {co.logo
                                ? <img src={co.logo} alt="" aria-hidden="true" loading="lazy" />
                                : <CompanyDot $c={co.color} aria-hidden="true">{co.initials}</CompanyDot>}
                              {co.name}
                            </CompanyBadge>
                            {job.area && <AreaBadge $bg={jc.bg} $color={jc.color} $border={jc.border}>{job.area}</AreaBadge>}
                            <LocalBadge>{job.local || "Remoto"}</LocalBadge>
                          </JTop>
                          <JTitle>{job.title}</JTitle>
                          {job.resumo && <JSummary>{job.resumo}</JSummary>}
                          <TagsRow>
                            {job.regime && <Tag>{job.regime}</Tag>}
                            {job.nivel && <Tag>{job.nivel}</Tag>}
                            {job.faixa && (job.regime || job.nivel) && <Sep aria-hidden="true">·</Sep>}
                            {job.faixa && <SalaryTag>{job.faixa}</SalaryTag>}
                          </TagsRow>
                        </JLeft>
                        <Arrow aria-hidden="true">→</Arrow>
                      </Job>
                    );
                  })}
                </List>
              </div>
            ))}
          </>
        )}
      </Shell>
    </Section>
  );
}
